import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ArrowLeft, Printer, FileText, Tag, Receipt } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";

export default function Documents() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [batch, setBatch] = useState(null);
  const [product, setProduct] = useState(null);
  const [client, setClient] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const printRef = useRef();

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const response = await api.get(`/batches/${id}/packing-list`);
      setBatch(response.data.batch);
      setProduct(response.data.product);
      setClient(response.data.client);
      setSupplier(response.data.supplier);
    } catch (error) {
      toast.error("Error al cargar los datos del lote");
      navigate("/batches");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return format(new Date(dateStr), "dd/MM/yyyy", { locale: es });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Lote no encontrado</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex items-center justify-between no-print">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/batches")} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Documentos</h1>
            <p className="text-slate-500 mt-1">Lote: {batch.numero_lote}</p>
          </div>
        </div>
        <Button onClick={handlePrint} className="bg-slate-900 hover:bg-slate-800" data-testid="print-button">
          <Printer className="w-4 h-4 mr-2" />
          Imprimir
        </Button>
      </div>

      <Tabs defaultValue="packing" className="no-print">
        <TabsList>
          <TabsTrigger value="packing" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Packing List
          </TabsTrigger>
          <TabsTrigger value="label" className="flex items-center gap-2">
            <Tag className="w-4 h-4" />
            Etiqueta
          </TabsTrigger>
          <TabsTrigger value="invoice" className="flex items-center gap-2">
            <Receipt className="w-4 h-4" />
            Factura
          </TabsTrigger>
        </TabsList>

        <TabsContent value="packing" className="mt-6">
          <Card className="border-slate-200 print-container" ref={printRef}>
            <CardHeader className="border-b border-slate-200 bg-slate-50">
              <CardTitle className="text-center text-xl">PACKING LIST</CardTitle>
            </CardHeader>
            <CardContent className="p-8 space-y-6">
              {/* Header info */}
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">REMITENTE / SHIPPER</h3>
                  <p className="font-medium">{supplier?.nombre || "LG GLOBAL SEAFOOD C.A."}</p>
                  <p className="text-sm text-slate-600">{supplier?.direccion || "-"}</p>
                  <p className="text-sm text-slate-600">RIF: {supplier?.identificacion_fiscal || "-"}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">DESTINATARIO / CONSIGNEE</h3>
                  <p className="font-medium">{client?.nombre || "-"}</p>
                  <p className="text-sm text-slate-600">{client?.direccion || "-"}</p>
                  <p className="text-sm text-slate-600">CIF: {client?.identificacion_fiscal || "-"}</p>
                </div>
              </div>

              {/* Shipment info */}
              <div className="border border-slate-200 rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b border-slate-200">
                      <td className="px-4 py-2 bg-slate-50 font-medium w-1/4">Nº LOTE</td>
                      <td className="px-4 py-2 font-mono">{batch.numero_lote}</td>
                      <td className="px-4 py-2 bg-slate-50 font-medium w-1/4">CONTENEDOR</td>
                      <td className="px-4 py-2 font-mono">{batch.contenedor || "-"}</td>
                    </tr>
                    <tr className="border-b border-slate-200">
                      <td className="px-4 py-2 bg-slate-50 font-medium">FECHA EMBARQUE</td>
                      <td className="px-4 py-2">{formatDate(batch.fecha_embarque)}</td>
                      <td className="px-4 py-2 bg-slate-50 font-medium">AWB/BL</td>
                      <td className="px-4 py-2 font-mono">{batch.awb_bl || "-"}</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 bg-slate-50 font-medium">FACTURA</td>
                      <td className="px-4 py-2 font-mono">{batch.factura_origen || "-"}</td>
                      <td className="px-4 py-2 bg-slate-50 font-medium">LLEGADA EST.</td>
                      <td className="px-4 py-2">{formatDate(batch.llegada_estimada)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Product details */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3">DESCRIPCIÓN DE MERCANCÍA</h3>
                <table className="w-full text-sm border border-slate-200">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold">PRODUCTO</th>
                      <th className="px-4 py-2 text-left font-semibold">PRESENTACIÓN</th>
                      <th className="px-4 py-2 text-right font-semibold">BULTOS</th>
                      <th className="px-4 py-2 text-right font-semibold">PESO NETO (kg)</th>
                      <th className="px-4 py-2 text-right font-semibold">PESO BRUTO (kg)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-slate-200">
                      <td className="px-4 py-3">{batch.producto_nombre || product?.nombre || "-"}</td>
                      <td className="px-4 py-3">{product?.presentacion || "-"}</td>
                      <td className="px-4 py-3 text-right font-mono">{batch.bultos || 0}</td>
                      <td className="px-4 py-3 text-right font-mono">{batch.peso_neto?.toFixed(2) || "0.00"}</td>
                      <td className="px-4 py-3 text-right font-mono">{batch.peso_bruto?.toFixed(2) || "0.00"}</td>
                    </tr>
                  </tbody>
                  <tfoot className="bg-slate-50 font-semibold">
                    <tr className="border-t border-slate-200">
                      <td colSpan="2" className="px-4 py-2">TOTAL</td>
                      <td className="px-4 py-2 text-right font-mono">{batch.bultos || 0}</td>
                      <td className="px-4 py-2 text-right font-mono">{batch.peso_neto?.toFixed(2) || "0.00"}</td>
                      <td className="px-4 py-2 text-right font-mono">{batch.peso_bruto?.toFixed(2) || "0.00"}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Additional info */}
              <div className="grid grid-cols-2 gap-8 text-sm">
                <div>
                  <p><span className="font-medium">País de origen:</span> {product?.pais_origen || "Venezuela"}</p>
                  <p><span className="font-medium">Zona FAO:</span> {product?.zona_fao || "-"}</p>
                  <p><span className="font-medium">Temperatura:</span> {batch.temperatura || "-18º C"}</p>
                </div>
                <div>
                  <p><span className="font-medium">Fecha de captura:</span> {formatDate(batch.fecha_captura)}</p>
                  <p><span className="font-medium">Fecha de caducidad:</span> {formatDate(batch.fecha_caducidad)}</p>
                </div>
              </div>

              {/* Observations */}
              {batch.observaciones && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">OBSERVACIONES</h3>
                  <p className="text-sm text-slate-600">{batch.observaciones}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="label" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Label preview */}
            <Card className="border-2 border-slate-900 print-container">
              <CardContent className="p-6 space-y-4">
                <div className="text-center border-b border-slate-200 pb-4">
                  <p className="text-xs text-slate-500 mb-1">NO CONGELAR UNA VEZ DESCONGELADO</p>
                  <h2 className="text-xl font-bold">{batch.producto_nombre || product?.nombre || "PRODUCTO"}</h2>
                  <p className="text-sm italic">{product?.nombre_cientifico || ""}</p>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium">LOTE:</span>
                    <span className="font-mono">{batch.numero_lote}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">PESO NETO:</span>
                    <span className="font-mono">{batch.peso_neto?.toFixed(2) || "0.00"} kg</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">PAÍS ORIGEN:</span>
                    <span>{product?.pais_origen || "Venezuela"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">ZONA FAO:</span>
                    <span>{product?.zona_fao || "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">ARTE DE PESCA:</span>
                    <span>{product?.arte_pesca || "-"}</span>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium">F. CONGELACIÓN:</span>
                    <span>{formatDate(batch.fecha_congelacion)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">CONSUMIR ANTES DE:</span>
                    <span className="font-semibold text-red-600">{formatDate(batch.fecha_caducidad)}</span>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-4 text-xs text-slate-600">
                  <p className="font-medium mb-1">CONSERVACIÓN:</p>
                  <p>{product?.temperatura_almacenaje || batch.temperatura || "Mantener congelado a -18º C"}</p>
                </div>

                {product?.alergenos && (
                  <div className="border-t border-slate-200 pt-4 text-xs">
                    <p className="font-medium text-red-600">ALÉRGENOS: {product.alergenos}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Info */}
            <Card className="border-slate-200 no-print">
              <CardHeader>
                <CardTitle className="text-lg">Información de Etiqueta</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <p className="text-slate-600">
                  Esta etiqueta contiene la información requerida para el etiquetado de productos pesqueros congelados según la normativa vigente.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
                  <p className="font-medium text-amber-800 mb-2">Información obligatoria:</p>
                  <ul className="text-amber-700 space-y-1 text-xs">
                    <li>• Denominación del producto</li>
                    <li>• Nombre científico</li>
                    <li>• Zona de captura (FAO)</li>
                    <li>• Arte de pesca</li>
                    <li>• País de origen</li>
                    <li>• Fecha de congelación</li>
                    <li>• Fecha de caducidad</li>
                    <li>• Condiciones de conservación</li>
                    <li>• Alérgenos</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="invoice" className="mt-6">
          <Card className="border-slate-200 print-container">
            <CardHeader className="border-b border-slate-200">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-xl mb-2">FACTURA</CardTitle>
                  <p className="text-sm text-slate-500">Nº {batch.factura_origen || batch.numero_lote}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm">Fecha: {formatDate(batch.fecha_embarque)}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-8 space-y-6">
              {/* Parties */}
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">DE:</h3>
                  <p className="font-medium">{supplier?.nombre || "LG GLOBAL SEAFOOD C.A."}</p>
                  <p className="text-sm text-slate-600">{supplier?.direccion || "-"}</p>
                  <p className="text-sm text-slate-600">RIF: {supplier?.identificacion_fiscal || "-"}</p>
                  {supplier?.email && <p className="text-sm text-slate-600">{supplier.email}</p>}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">FACTURAR A:</h3>
                  <p className="font-medium">{client?.nombre || "-"}</p>
                  <p className="text-sm text-slate-600">{client?.direccion || "-"}</p>
                  <p className="text-sm text-slate-600">CIF: {client?.identificacion_fiscal || "-"}</p>
                  {client?.email && <p className="text-sm text-slate-600">{client.email}</p>}
                </div>
              </div>

              {/* Items */}
              <table className="w-full text-sm border border-slate-200">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold">DESCRIPCIÓN</th>
                    <th className="px-4 py-2 text-right font-semibold">CANTIDAD (kg)</th>
                    <th className="px-4 py-2 text-right font-semibold">PRECIO/kg</th>
                    <th className="px-4 py-2 text-right font-semibold">TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-slate-200">
                    <td className="px-4 py-3">
                      <p className="font-medium">{batch.producto_nombre || product?.nombre || "-"}</p>
                      <p className="text-xs text-slate-500">Lote: {batch.numero_lote}</p>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{batch.peso_neto?.toFixed(2) || "0.00"}</td>
                    <td className="px-4 py-3 text-right font-mono">€{product?.precio_venta?.toFixed(2) || "0.00"}</td>
                    <td className="px-4 py-3 text-right font-mono">
                      €{((batch.peso_neto || 0) * (product?.precio_venta || 0)).toFixed(2)}
                    </td>
                  </tr>
                </tbody>
                <tfoot className="bg-slate-50">
                  <tr className="border-t border-slate-200">
                    <td colSpan="3" className="px-4 py-2 text-right font-semibold">SUBTOTAL</td>
                    <td className="px-4 py-2 text-right font-mono">
                      €{((batch.peso_neto || 0) * (product?.precio_venta || 0)).toFixed(2)}
                    </td>
                  </tr>
                  <tr className="border-t border-slate-200">
                    <td colSpan="3" className="px-4 py-2 text-right font-semibold">IVA (0%)</td>
                    <td className="px-4 py-2 text-right font-mono">€0.00</td>
                  </tr>
                  <tr className="border-t-2 border-slate-300 text-lg">
                    <td colSpan="3" className="px-4 py-3 text-right font-bold">TOTAL</td>
                    <td className="px-4 py-3 text-right font-mono font-bold">
                      €{((batch.peso_neto || 0) * (product?.precio_venta || 0)).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>

              {/* Notes */}
              <div className="text-sm text-slate-600">
                <p className="font-medium mb-1">NOTAS:</p>
                <p>• Mercancía exenta de IVA por exportación</p>
                <p>• Contenedor: {batch.contenedor || "-"}</p>
                <p>• AWB/BL: {batch.awb_bl || "-"}</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
